import { useEffect, useState } from "react";
import Footer from "../../components/Footer/Footer";
import "./admin.css";

const Admin = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(12);
  const [lastResponse, setLastResponse] = useState({ items: [], total: 0 });
  const [searchInput, setSearchInput] = useState("");
  const [filterType, setFilterType] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [stats, setStats] = useState({ totalItems: "—", totalUsers: "—" });
  const API = {
    items: "/api/admin/items",
    users: "/api/admin/users/summary",
  };
  const safeFetch = async (url, opts = {}) => {
    opts.credentials = opts.credentials || "include";
    opts.headers = opts.headers || { Accept: "application/json" };
    const resp = await fetch(url, opts);
    if (!resp.ok) throw new Error("Server error: " + resp.status);
    return resp.json();
  };

  const buildQuery = (params) => {
    const q = new URLSearchParams(params);
    return "?" + q.toString();
  };
  const loadData = async (page = 1) => {
    setCurrentPage(page);
    const q = searchInput.trim();
    const url =
      API.items +
      buildQuery({
        q,
        type: filterType,
        sort: sortBy,
        page,
        pageSize,
      });

    try {
      const data = await safeFetch(url);
      setLastResponse(data);
      setStats((prev) => ({ ...prev, totalItems: data.total || "—" }));
    } catch (err) {
      console.error(err);
    }
  };

  const loadSummary = async () => {
    try {
      const data = await safeFetch(API.users);
      setStats((prev) => ({ ...prev, totalUsers: data.totalUsers || "—" }));
    } catch (err) {
      console.error(err);
    }
  };
  const handleShowDetails = async (id) => {
    try {
      const data = await safeFetch(API.items + "/" + encodeURIComponent(id));
      setSelectedItem(data);
      setShowDetails(true);
    } catch (err) {
      alert("Failed to fetch details");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Permanently delete item? This action is logged."))
      return;
    try {
      await safeFetch(API.items + "/" + encodeURIComponent(id), {
        method: "DELETE",
      });
      alert("Deleted. Refreshing list.");
      loadData(currentPage);
    } catch (err) {
      console.error(err);
      alert("Delete failed.");
    }
  };

  const handleExport = (rows, filename = "export.csv") => {
    if (!rows || rows.length === 0) return;
    const headers = Object.keys(flatten(rows[0]));
    const csv = [
      headers.join(","),
      ...rows.map((r) =>
        headers.map((h) => JSON.stringify(flatten(r)[h] ?? "")).join(","),
      ),
    ].join("\\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleSignOut = async () => {
    try {
      await fetch("/auth/signout", { method: "POST", credentials: "include" });
    } finally {
      window.location.href = "/";
    }
  };
  const flatten = (obj, prefix = "") => {
    const out = {};
    for (const k in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
      const val = obj[k];
      const key = prefix ? prefix + "." + k : k;
      if (val && typeof val === "object" && !Array.isArray(val)) {
        Object.assign(out, flatten(val, key));
      } else {
        out[key] = val;
      }
    }
    return out;
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d)) return String(iso);
    return d.toLocaleString();
  };
  useEffect(() => {
    loadSummary();
    loadData(1);
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "dark") {
      document.body.classList.add("dark");
    }
  }, []);

  return (
    <div className="admin-container">
      <header className="admin-topbar">
        <div className="brand">Admin</div>
        <div className="admin-actions">
          <button onClick={() => loadData(currentPage)} title="Refresh data">
            ⟳ Refresh
          </button>
          <button
            onClick={() =>
              handleExport(lastResponse.items || [], "scantaps_export.csv")
            }
            title="Export visible rows to CSV"
          >
            ⬇ Export CSV
          </button>
          <button onClick={handleSignOut} title="Sign out">
            Sign Out
          </button>
        </div>
      </header>

      <main className="admin-main">
        <section className="controls">
          <div className="search">
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name, ASU ID, item name, location..."
            />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">All types</option>
              <option value="found">Found</option>
              <option value="lost">Lost</option>
            </select>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="recent">Most recent</option>
              <option value="oldest">Oldest</option>
              <option value="location">Location</option>
            </select>
            <button onClick={() => loadData(1)}>Apply</button>
          </div>

          <div className="stats">
            <div className="stat">
              <div className="num">{stats.totalItems}</div>
              <div className="label">Total Items</div>
            </div>
            <div className="stat">
              <div className="num">{stats.totalUsers}</div>
              <div className="label">Total Users</div>
            </div>
          </div>
        </section>

        <section className="table-wrap" aria-live="polite">
          <table className="admin-table" aria-live="polite">
            <thead>
              <tr>
                <th>#</th>
                <th>Item / Title</th>
                <th>Type</th>
                <th>Found/Reported At</th>
                <th>Location</th>
                <th>Owner (ASU ID)</th>
                <th>Contact</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {lastResponse.items.length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty">
                    No items found
                  </td>
                </tr>
              ) : (
                lastResponse.items.map((item, idx) => (
                  <tr key={item.id}>
                    <td>{(currentPage - 1) * pageSize + idx + 1}</td>
                    <td className="item-title">
                      {item.title || item.name || "—"}
                    </td>
                    <td>{item.type || "—"}</td>
                    <td>{formatDate(item.reportedAt)}</td>
                    <td>{item.location || "—"}</td>
                    <td>
                      {item.ownerName || "—"}
                      <br />
                      <small>{item.ownerASUID || "—"}</small>
                    </td>
                    <td>{item.contact || "—"}</td>
                    <td className="actions">
                      <button onClick={() => handleShowDetails(item.id)}>
                        View
                      </button>
                      <button onClick={() => handleDelete(item.id)}>
                        Delete
                      </button>
                      <button
                        onClick={() =>
                          handleExport([item], `item-${item.id}.csv`)
                        }
                      >
                        Export
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>

        {showDetails && selectedItem && (
          <section className="details-pane" aria-hidden="false">
            <button className="close" onClick={() => setShowDetails(false)}>
              ✕
            </button>
            <div className="details-content">
              <h3>{selectedItem.title || selectedItem.name}</h3>
              <p>
                <b>Type:</b> {selectedItem.type || ""}
              </p>
              <p>
                <b>Reported At:</b> {formatDate(selectedItem.reportedAt)}
              </p>
              <p>
                <b>Location:</b> {selectedItem.location}
              </p>
              <p>
                <b>Description:</b>
                <br />
                {selectedItem.description || ""}
              </p>
              <p>
                <b>Owner:</b> {selectedItem.ownerName || "—"} (
                {selectedItem.ownerASUID || "—"})
              </p>
              <p>
                <b>Contact:</b> {selectedItem.contact || "—"}
              </p>
              <p>
                <b>Metadata:</b>
                <br />
                <small>
                  {JSON.stringify(selectedItem.metadata || {}, null, 2)}
                </small>
              </p>
              <div className="detail-actions">
                <button
                  onClick={() =>
                    window.open(
                      `/admin/items/${encodeURIComponent(selectedItem.id)}/raw`,
                      "_blank",
                    )
                  }
                >
                  Open raw
                </button>
                <button
                  onClick={() =>
                    (window.location.href = `/admin/claims?item=${encodeURIComponent(
                      selectedItem.id,
                    )}`)
                  }
                >
                  View claims
                </button>
              </div>
            </div>
          </section>
        )}

        <nav className="pagination">
          {Array.from(
            { length: Math.ceil(lastResponse.total / pageSize) || 1 },
            (_, i) => (
              <button
                key={i + 1}
                disabled={i + 1 === currentPage}
                onClick={() => loadData(i + 1)}
              >
                {i + 1}
              </button>
            ),
          )}
        </nav>
      </main>

      <Footer />
    </div>
  );
};

export default Admin;
